import { Grid, Paper, Stack, Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import usePrimaryColor from "../../hooks/usePrimaryColor";
import ReadOnlyTextField from "./ReadOnlyTextField";
import { overflowingTitleSx } from "../../enums/Styles";

export default function DetailCard({
  valueArray,
  value,
  title,
  grid = false,
  gridCount = 3,
  gridSpacing = 1,
  stackSpacing = 1,
  elevation,
  variant,
  titleColor,
  fontWeight,
  paper = true,
  minHeight = 60,
  minHeightPaper = 70,
  readOnlyTextArea = false,
  testId,
}) {
  const { t } = useTranslation();
  const isLink = (text) => {
    const httpsPattern =
      /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/; // URL regex
    const httpPattern = /^(http?:\/\/)?(www\.)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}/; // URL regex
    const isUrl = httpPattern.test(text) || httpsPattern.test(text);
    return typeof text === "string" && isUrl;
  };

  const title_color = usePrimaryColor();

  const isEmail = (text) => {
    const emailPattern = /.+@.+\..+/; // Basic email regex
    return typeof text === "string" && emailPattern.test(text);
  };

  const isPhone = (text) => {
    if (typeof text !== "string") return false;
    const clean = text.replace(/[\s\-().]/g, ""); // Remove spaces/dashes/parens
    return /^\+?[0-9]{6,15}$/.test(clean);
  };

  const Card = ({ title, value, testId }) => {
    const isOver300Chars = value?.length > 300; // Check if the value exceeds 300 characters
    if (typeof value !== null) {
      if (!paper) {
        return (
          <Stack
            data-testid={testId}
            sx={{
              minHeight: minHeight,
              padding: 1,
              mb: 1,
              flexGrow: 1, // Makes Paper fill its parent (Grid item)
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography color={title_color} fontWeight={fontWeight}>
              <small>{t(title)}</small>
            </Typography>

            {isEmail(value) ? (
              <Link
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `mailto:${value}`;
                }}
              >
                {value}
              </Link>
            ) : isPhone(value) ? (
              <Link
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `tel:${value}`;
                }}
              >
                {value}
              </Link>
            ) : isLink(value) ? (
              <CustomLink value={value} />
            ) : isOver300Chars ? (
              <ReadOnlyTextField data={value} />
            ) : (
              <Typography
                sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
              >
                {value}
              </Typography>
            )}
          </Stack>
        );
      } else if (String(value).trim()) {
        return (
          <Paper
            data-testid={testId}
            elevation={elevation}
            variant={variant}
            sx={{
              minHeight: minHeightPaper,
              padding: 1,
              mb: 1,
              flexGrow: 1, // Makes Paper fill its parent (Grid item)
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              color={title_color}
              fontWeight={fontWeight}
              sx={overflowingTitleSx}
            >
              <small>{t(title)}</small>
            </Typography>

            {isEmail(value) ? (
              <Link
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `mailto:${value}`;
                }}
              >
                <Box sx={overflowingTitleSx}>{value}</Box>
              </Link>
            ) : isPhone(value) ? (
              <Link
                color="inherit"
                onClick={(e) => {
                  e.preventDefault();
                  window.location.href = `tel:${value}`;
                }}
              >
                <Box sx={overflowingTitleSx}>{value}</Box>
              </Link>
            ) : isLink(value) ? (
              <CustomLink value={value} />
            ) : isOver300Chars ? (
              <ReadOnlyTextField data={value} />
            ) : (
              <Typography
                sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
              >
                {value}
              </Typography>
            )}
          </Paper>
        );
      }
    }
  };

  if (Array.isArray(valueArray) && valueArray?.length > 0) {
    if (grid) {
      return (
        <Grid container spacing={gridSpacing} alignItems="stretch">
          {valueArray.map((item, index) => {
            const isLastRow =
              Math.floor(index / gridCount) ===
              Math.floor((valueArray?.length - 1) / gridCount);
            const remainingItems = valueArray?.length % gridCount || gridCount;
            const xsValue = isLastRow
              ? Math.floor(12 / remainingItems)
              : 12 / gridCount;

            if (item.value != null) {
              return (
                <Grid key={index} item xs={xsValue} display="flex">
                  <Card
                    value={item.value}
                    title={item.title}
                    testId={item.testId}
                  />
                </Grid>
              );
            }
          })}
        </Grid>
      );
    } else {
      return (
        <Stack spacing={stackSpacing}>
          {valueArray.map((item, index) => {
            if (item.value != null) {
              return (
                <Card
                  key={index}
                  title={item.title}
                  value={item.value}
                  testId={item.testId}
                />
              );
            }
          })}
        </Stack>
      );
    }
  }

  return <Card title={title} value={value} testId={testId} />;
}

function CustomLink({ value }) {
  const normalizedValue =
    value.startsWith("http://") || value.startsWith("https://")
      ? value
      : `https://${value}`;
  return (
    <a href={normalizedValue} target="_blank" rel="noopener noreferrer">
      <p>{value}</p>
    </a>
  );
}
